/**
 * The Document — ADR-006 §3: the serializable, reproducible statement of a
 * piece of animation work. What was imported, how it was corrected, what the
 * user authored. Plain data, zod-gated, deep-frozen: the scene is a VIEW over
 * this; nothing here may reference an engine object, and nothing outside the
 * command log (commands.ts `applyCommand`, ADR-007) may write it — §2.3 made
 * real; the frozen tree refuses every other door.
 *
 * One Document per SPECIMEN, carrying N clip selections (§12.3, decided
 * 2026-07-23). The corpus manifest is a list of these plus expected results —
 * the same schema by construction, or the ADR has failed (§3.1).
 *
 * Two doors, two verbs of trust (ADR-007 §4): `parseDocument` LOADS — raw
 * data begins a lineage, and a caller hand-assembling raw data has authored a
 * new FILE, which trusts its author. `applyCommand` (commands.ts) EDITS —
 * the only Document→Document path within a lineage; undo/dirty/replay are
 * lineage-scoped. In the app, only the loader/store imports parseDocument.
 * Depends on: zod, kernel IR (schemas + errors). Depended on by: the app's
 * session driver, headless/agent runs (P4), the corpus manifest.
 */
import { z } from 'zod';
import { SchemaValidationError } from '../ir/errors.js';
import { quatDataSchema } from '../ir/rig-descriptor.js';
import { vec3Schema } from '../ir/ontology.js';
import { ARM_SPACE_MAX_DEGREES } from './arm-space.js';
import { HEAD_SPACE_MAX_DEGREES } from './head-space.js';
import type { SeparationProfile } from '../hands/separation-profile.js';
import type { DigitName } from '../hands/place-digits.js';
import { STUB_COUNT_MAX, STUB_COUNT_MIN, TAIL_COUNT_MAX, TAIL_COUNT_MIN } from '../tails/stations.js';
import { tailSpringSettingsSchema } from '../springs/spring-drive.js';

/**
 * The hand-profile vocabulary a confirmation may declare — bound to the
 * analyzer's own type via `satisfies`, so the two cannot drift silently.
 */
export const HAND_PROFILE_VALUES = [
  'hand-5',
  'hand-4',
  'hand-3',
  'hand-partial',
  'hand-mitten',
  'hand-none',
] as const satisfies readonly SeparationProfile[];

/**
 * The digit vocabulary a `digitAssignment` confirmation may name — bound to the
 * placer's own `DigitName` via `satisfies`, so the Document and the kernel that
 * consumes it cannot drift.
 */
export const DIGIT_NAMES = ['Thumb', 'Index', 'Middle', 'Ring', 'Pinky'] as const satisfies readonly DigitName[];

/**
 * Where the specimen bytes come from. The Document names the work; resolving
 * a uri to bytes is the driver's job (app fetch, node fs, corpus store) — and
 * a uri that resolves to nothing fails at load, loudly, naming it (§8).
 */
export const specimenRefSchema = z.object({
  /** Driver-resolvable location: corpus fixture name, path, or URL. */
  uri: z.string().min(1),
  /**
   * 'bvh' and 'anim' (SL LLKeyframeMotion) are SOURCE formats — mesh-less
   * animation carriers (ADR-003 §12); a target specimen must carry a mesh,
   * and runDocument refuses them there by name.
   */
  format: z.enum(['glb', 'fbx', 'bvh', 'anim']),
  /** Integrity pin. When present the driver MUST verify before import. */
  sha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .optional(),
});
export type SpecimenRef = z.infer<typeof specimenRefSchema>;

/**
 * A value that moves vertices is recorded with its provenance — a default is
 * a DECLARATION, not an absence (§8). A run can never be reproduced
 * differently by a later change of default, because the default it ran with
 * is in the Document.
 */
export const declaredNumberSchema = z.object({
  value: z.number(),
  provenance: z.enum(['declared', 'default']),
});
export type DeclaredNumber = z.infer<typeof declaredNumberSchema>;

/**
 * A human act, recorded (§8, §12.2): the per-affordance confirmation that
 * releases a low-confidence read. The `human:` namespace is structural — the
 * engine constructs Documents through code paths that never mint one, and an
 * unattended run over unconfirmed reads refuses rather than self-confirms.
 *
 * Trust boundary, stated plainly (Codex P1, #50): a Document is a project
 * file, and a file trusts its author — the schema cannot cryptographically
 * attest humanity, and does not pretend to. What §8 forbids, and what this
 * repo makes structurally true, is THIS engine signing its own homework:
 * no code path in the tree writes `confirmedBy`, and the UI writer gates on
 * a trusted user event (the guide's `isTrusted` rule, brief §5.6). A person
 * who hand-forges a confirmation in their own file has confirmed it.
 */
export const confirmationSchema = z.object({
  /**
   * The affordance-feeding value this confirmation releases.
   *
   * `digitAssignment` is ONE PER DIGIT, value `"<DigitName>@<x>,<y>,<z>"` — a
   * named tip in BIND-WORLD, five of them naming a whole hand. Each digit is
   * its own human act ("yes, that is the ring fingertip"), carries its own
   * `confirmedBy`, and lapses on its own. A tip nobody names is not a digit,
   * which is how a dorsal arch or a phantom finger is set aside.
   *
   * IT USED TO BE `"<branch-id>:<DigitName>"`, AND THAT WAS A BUG IN THE SAVE
   * FORMAT (ADR-022 §3.2). Branch ids are minted per analysis and renumber
   * whenever the resolution moves, so a saved Document pinned its answers to
   * labels that do not survive re-measurement — the Codex #77 hazard, fixed in
   * the panel's memory but still latent one layer down, in the file. A tip is
   * a place on the model: resolution-independent, re-openable in a year, and
   * the thing a person actually pointed at. {@link parseDigitPin} is the one
   * definition of the grammar; old-format values refuse there, by name.
   */
  /**
   * `wristRecenter` (value: the method, today only `'seed-centroid'`) is the
   * first unbind-adjust-rebind declaration (2026-07-28): move the hand bone's
   * REST to the wrist volume's centre — rest+IBM together, so the mesh does
   * not move; only the rotation pivot does — because generated rigs put the
   * wrist joint in FRONT of the wrist flesh (measured variance, universal on
   * the Tripo corpus). A human act like every confirmation: the panel
   * proposes with the measured offset, a trusted click signs.
   */
  /**
   * `digitKnuckle` (ADR-024 §2.2) is the OPTIONAL second row: same grammar as
   * `digitAssignment` (`"<DigitName>@<x>,<y>,<z>"`, a BIND-WORLD point), one
   * per digit, naming where that digit's FIRST KNUCKLE is. It rides beside the
   * digit's tip assignment and is meaningless without one — a knuckle names a
   * row position on a digit somebody pinned, so a knuckle for an unpinned
   * digit refuses at parse. Optional per digit: an unknuckled digit routes
   * wrist→tip exactly as before.
   */
  /**
   * `wristStation` (ADR-025) — WHERE THE WRIST IS, as a BIND-WORLD point
   * (`"Wrist@<x>,<y>,<z>"`, the pin grammar with a fixed name), one per hand.
   * Tripo drops the hand joint into the palm on some characters, and every
   * downstream measurement anchors to that joint; this is the person's answer
   * ("this is the wrist"), clicked on the model. Only its station ALONG the arm
   * is used — the lateral seat stays the recentre's job. Absent, the joint
   * stands, bit-identical (D4: keep what is not declared a problem as it was).
   */
  /**
   * `thumbOpposition` (field 2026-08-15) — the thumb's frame opposition in
   * DEGREES (an integer string in −180…180): how far its dorsal is rotated
   * from the fingers' palm normal toward the radial edge. Per hand, because it
   * is per rig: a cupped, wrist-flexed bind is already partly opposed. Absent,
   * the kernel default (90) applies.
   */
  target: z.enum(['thumbBranch', 'handProfile', 'digitAssignment', 'wristRecenter', 'digitKnuckle', 'wristStation', 'thumbOpposition']),
  /** Which declared hand it applies to — a confirmation names what was examined. */
  scope: z.string().min(1),
  value: z.string().min(1),
  confirmedBy: z.string().regex(/^human:\S+$/),
  /**
   * Digest of the evidence the person saw (§8: a confirmation binds to the
   * MEASUREMENT it answered, and lapses loudly when the measurement moves).
   * The digest pipeline is not designed yet — a driver encountering one MUST
   * refuse via notDesignedYet rather than ignore it. Reserved, never silent.
   */
  evidenceDigest: z.string().optional(),
});
export type Confirmation = z.infer<typeof confirmationSchema>;

/** A digit target read off a `digitAssignment` confirmation: a named BIND-WORLD tip. */
export interface DigitPinValue {
  readonly digit: string;
  readonly tip: { readonly x: number; readonly y: number; readonly z: number };
}

/** The old branch-keyed grammar, kept ONLY to recognise and refuse it (ADR-022 D3). */
const LEGACY_BRANCH_PIN = /^branch-\d+:\w+$/;
const NUM = String.raw`-?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?`;
const DIGIT_PIN = new RegExp(`^(\\w+)@(${NUM}),(${NUM}),(${NUM})$`);

/**
 * Parse `"<DigitName>@<x>,<y>,<z>"` — THE one definition of the pin grammar
 * (ADR-022 §7 gate 9). Returns null for anything that is not a well-formed pin;
 * the caller decides how loudly to say so, and `parseDocument` says so by name.
 *
 * Deliberately NOT lenient about the legacy `"branch-N:Digit"` form: converting
 * it silently would re-aim a person's answer at whatever geometry that branch id
 * happens to label today, which is the exact failure the format change exists to
 * end. {@link isLegacyDigitPin} lets the caller name the migration instead.
 */
export function parseDigitPin(value: string): DigitPinValue | null {
  const m = DIGIT_PIN.exec(value);
  if (m === null) return null;
  const [x, y, z] = [Number(m[2]), Number(m[3]), Number(m[4])];
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;
  return { digit: m[1]!, tip: { x, y, z } };
}

/** True for the pre-ADR-022 `"branch-N:Digit"` value — recognised so it can refuse by name. */
export function isLegacyDigitPin(value: string): boolean {
  return LEGACY_BRANCH_PIN.test(value);
}

/** The fixed "digit" name a `wristStation` value carries — the pin grammar, one row, no digit. */
export const WRIST_STATION_NAME = 'Wrist';

/** Format a pin for the Document — the writer's half of {@link parseDigitPin}. */
export function formatDigitPin(digit: string, tip: { x: number; y: number; z: number }): string {
  const n = (v: number): string => Number(v.toFixed(6)).toString();
  return `${digit}@${n(tip.x)},${n(tip.y)},${n(tip.z)}`;
}

/**
 * Per-hand Hand Studio session params (ADR-010 §11) — PER HAND because
 * generated rigs are not symmetric (the kobold: R@40, L@32). The thumb pin
 * and profile declaration are NOT here: they are human acts and live in
 * `confirmations`, keyed to this hand's bone — the analyzer's own guess can
 * never flow back in as if a person had pinned it (the kobold fabrication).
 */
export const handSessionSchema = z.object({
  /** The rig's hand bone — validated against the loaded rig, loudly (§8). */
  handBone: z.string().min(1),
  forearmBone: z.string().min(1).nullable(),
  side: z.enum(['left', 'right']),
  /** Voxel resolution — geometry-moving, therefore provenanced (§8). */
  resolution: declaredNumberSchema,
  /**
   * REPLACE FINGERS (Allen, 2026-09-29): the hand already has digits — a reopened export's graft, or a
   * Tripo preset's own — and a person chose to replace them. The run strips the hand's mapped digit
   * chains (their flesh folds into the hand) BEFORE it grafts from the pins. Absent = the hand is
   * fingerless and nothing is stripped (every Document written before this, unchanged).
   */
  nativeDigits: z.literal('replace').optional(),
});
export type HandSession = z.infer<typeof handSessionSchema>;

/** The specimen being animated — the Document's subject (§12.3). */
/**
 * A tier-2 joint recentre: the joint's rotation pivot moves into the flesh
 * while the mesh stays bit-identical (rest and inverse-bind travel together —
 * `recenterBoneBind` and its scene twin).
 *
 * WHY THIS IS NOT A CONFIRMATION. `wristRecenter` rides `confirmations`, whose
 * `scope` is gated to a DECLARED HAND — an elbow is not a hand and never will
 * be, so the hand door cannot carry it. This slice is joint-general, which is
 * also what the freehand joint gizmo will need. The wrist deliberately stays
 * where it is; migrating it is a separate change with its own evidence.
 *
 * THE METHOD IS THE DECLARATION, NOT THE NUMBER. `method` names how to
 * re-derive the position ('limb-axis' — the flesh centroid at the joint's own
 * station; 'cap-height' — the shoulder: the Upperarm raised within its cap to
 * the A-pose band, declared on the node in extras.poqpoq.shoulderRecenter), so a re-import, a re-scaled rig, or an edited mesh recomputes
 * rather than replaying a stale coordinate. Same posture as the wrist's
 * 'seed-centroid' (§8: the value a run reproduces is the value the author
 * wrote).
 *
 * `authoredOffset` is the ONE exception, and it exists because the proposal UI
 * lets a person drag the pivot: when they do, THEIR placement is the answer
 * and no measurement may overwrite it (ADR-010 §11, the hand wins). It is
 * stored as a FRACTION of the limb's own length in the joint's bind frame, not
 * as world millimetres — a ratio survives a rescaled or regenerated character,
 * where a raw coordinate silently becomes a lie. The measured value is not
 * stored beside it: it is re-derived and shown as evidence, so the two can
 * never drift apart in the file.
 */
export const jointRecenterSchema = z.object({
  /** The rig bone whose pivot moves. Checked against the live rig by the driver. */
  bone: z.string().min(1),
  /** How to re-derive the target position. The driver refuses a method it does not implement. */
  method: z.string().min(1),
  /** A human's own placement, as a fraction of limb length in the joint's bind frame. */
  authoredOffset: vec3Schema.optional(),
  /** Explicit elbow placement: rebuild the old helper paint and crease at the new pivot. */
  rebuildHinge: z.boolean().optional(),
  confirmedBy: z.string().regex(/^human:\S+$/),
});
export type JointRecenter = z.infer<typeof jointRecenterSchema>;

/**
 * A TAIL (ADR-031 §3.3a, D2 ruled 2026-09-22): a durable declaration that a
 * tail zone grows a chain, replayed by the run on every load — the hand
 * graft's shape (`target.hands` + confirmations), not a baked chain. The
 * chain itself is DERIVED each run from the zone's faces and this record, so
 * a reopened Document re-places it and "more bones" is one number changing.
 *
 * WHY NOT A CONFIRMATION. `confirmations.scope` is gated to a declared hand;
 * tails get their own slot and their own `confirmedBy` (ADR-031 §6, the
 * `recenters` precedent).
 *
 * `count` is a `DeclaredNumber`: `default` when the measured rule chose it
 * (length over mean radius, clamped to [3, 16]), `declared` once a person
 * moved it (D4 — the flyout's more / fewer bones). `resolution` is the voxel
 * knob, absent = the kernel's default. `root` / `tip` are a person's
 * bind-world pins that correct the measurement — absent = measured.
 *
 * The zone id is spelled inline (the zone schemas are declared further down
 * this file); the structural gate in `parseDocument` checks it names a zone
 * that a person confirmed as a tail.
 */
export const tailDeclarationSchema = z.object({
  zoneId: z.string().regex(/^z[1-9]\d*$/),
  count: declaredNumberSchema,
  resolution: declaredNumberSchema.optional(),
  root: vec3Schema.optional(),
  tip: vec3Schema.optional(),
  /**
   * CENTRE THE TAIL (2026-09-24): the yaw, degrees about world +Y through the
   * root, the tail's rest flesh is turned by before the chain is placed —
   * measured once when a person asked, then REPLAYED on every run and reopen,
   * never re-measured (the ADR-030 §14 lesson). Absent = as the file has it.
   */
  recentre: z.object({ degrees: z.number().min(-180).max(180) }).optional(),
  /**
   * GROUP THE FAN (2026-09-25, the kitsune): several tails from one root are
   * rigged as ONE chain through the bundle's centre of mass — a person's
   * choice at the fan refusal, replayed on every run. Absent = a fan refuses.
   */
  fan: z.literal('grouped').optional(),
  /**
   * PLACE THE ROOT (2026-10-05, the lemur): where Tail1 pivots, bind-world —
   * dragged by a person on the tail's flag, replayed on every run. Only the
   * pivot moves; the base blends hip → tail over its distance from the measured
   * root (kernel `tailPivotSeam`). Absent = the measured root on the skin.
   */
  pivot: vec3Schema.optional(),
  confirmedBy: z.string().regex(/^human:\S+$/),
});
export type TailDeclaration = z.infer<typeof tailDeclarationSchema>;

/**
 * A SOFT-TISSUE LEAF (ADR-038 J2): a durable declaration that a region —
 * a breast, the belly, a glute — grows a jiggle leaf, replayed by the run on
 * every load (the tail's shape: the leaf, its weights and its spring are
 * DERIVED each run from the body and this record). `zoneId` names a zone a
 * person called soft tissue; its faces then ARE the region (a declared zone
 * overrides the detector). Absent = the detector's measurement.
 *
 * The region ids are spelled inline (the kernel's jiggle module owns them;
 * the Document must not import the detector) and pinned equal by a test.
 * The spring's knobs are NOT here: they are session state and the file's
 * (ADR-038 R3, the tail's posture until that ruling lands).
 */
export const softTissueDeclarationSchema = z.object({
  region: z.enum(['LeftBreast', 'RightBreast', 'Belly', 'LeftGlute', 'RightGlute']),
  zoneId: z.string().regex(/^z[1-9]\d*$/).optional(),
  confirmedBy: z.string().regex(/^human:\S+$/),
});
export type SoftTissueDeclaration = z.infer<typeof softTissueDeclarationSchema>;

/**
 * HAIR (ADR-039 T1, Allen's rulings 2026-09-30): a person confirmed that a
 * region is hair. Hair is its OWN region kind — not a tail, not cloth, not soft
 * tissue — because once confirmed Harry discards the generator's weights there
 * and assigns its own, and the treatment is chosen by how the hair hangs.
 *
 *  - `zoneId` names the region: a zone whose intent is `hair` (the face set,
 *    with the mesh fingerprint that refuses a regenerated character by name).
 *    `hair.confirm` writes the zone and this record together, so one undo
 *    takes both away.
 *  - `skull` is the confirmed skull in BIND-WORLD: centre, semi-axes along the
 *    body's (left, up, forward), and the body's facing they were read in.
 *    `adjusted` = a person dragged or scaled it; `measured` = they confirmed
 *    the measurement. A reopen reads it back, never re-measures it (LAWS: a
 *    heuristic re-derived on load must tell a declaration from a fact).
 *  - `treatment` is the connectedness verdict the person confirmed: attached
 *    (J4 jiggle), free (spring chains) or mixed (both) — T2 builds on it.
 *  - `gravity` (0..1) is a SETTING, default 0 = HOLD THE SCULPT: the rest
 *    shape is the equilibrium, so floating or anime hair stays up unless the
 *    author turns gravity up (Allen, 2026-09-30).
 *  - `motion` (optional; ADR-039 §14): the softened hair's Bounce / Sway /
 *    Jiggle, each 0..3, as the person set them on the card. Absent = the
 *    defaults ({@link HAIR_MOTION_DEFAULT}). A SETTING in the Document — not
 *    session state on the run — so a new clip, a re-run, a re-confirm and a
 *    reload all keep it (Allen, 2026-10-01: "when I add an animation it resets").
 *
 * The treatment vocabulary is spelled inline (the kernel's hair module owns
 * it; the Document must not import the detector) and pinned equal by a test.
 * Unconfirmed = ABSENT: an offer nobody confirmed records nothing.
 */
export const HAIR_GRAVITY_DEFAULT = 0;
export const hairGravitySchema = z
  .number()
  .finite()
  .refine((g) => g >= 0 && g <= 1, { message: 'hair gravity is bounded to [0, 1] — refused, never clamped silently' });
/** Allen's settings on black lace #6 after two field reads (2026-10-01): bounce ≈ 1, sway ≈ 0.8, jiggle ≈ 0.4. */
export const HAIR_MOTION_DEFAULT = { bounce: 1, sway: 0.8, jiggle: 0.4 } as const;
const hairMotionAmount = z
  .number()
  .finite()
  .refine((x) => x >= 0 && x <= 3, { message: 'hair motion is bounded to [0, 3] — refused, never clamped silently' });
export const hairMotionSchema = z.object({ bounce: hairMotionAmount, sway: hairMotionAmount, jiggle: hairMotionAmount });
export type HairMotion = z.infer<typeof hairMotionSchema>;
const positiveLength = z.number().finite().positive();
export const skullDeclarationSchema = z.object({
  centre: vec3Schema,
  semiAxes: z.object({ x: positiveLength, y: positiveLength, z: positiveLength }),
  yawDeg: z.number().min(-180).max(180),
  provenance: z.enum(['measured', 'adjusted']),
  /** How it was placed (the eye bones, the neck method, a person's drag) — evidence for the status line. */
  method: z.string().min(1),
});
export type SkullDeclaration = z.infer<typeof skullDeclarationSchema>;
export const hairTreatmentSchema = z.enum(['attached', 'free', 'mixed']);
export const hairDeclarationSchema = z.object({
  zoneId: z.string().regex(/^z[1-9]\d*$/),
  skull: skullDeclarationSchema,
  treatment: hairTreatmentSchema,
  gravity: hairGravitySchema,
  motion: hairMotionSchema.optional(),
  confirmedBy: z.string().regex(/^human:\S+$/),
});
export type HairDeclaration = z.infer<typeof hairDeclarationSchema>;

export const targetSessionSchema = z.object({
  label: z.string().min(1),
  specimen: specimenRefSchema,
  /**
   * Declared platform family (graft joint-frame priors + retarget key on it).
   * Detection from rig signature is ADR-004 territory; until it lands this is
   * an explicit claim, and it reads as one.
   */
  family: z.string().min(1),
  /** Ontology coverage profile the import aims for. Explicit, never implied. */
  profile: z.string().min(1),
  selectSkeleton: z.string().min(1).optional(),
  /**
   * FACE FORWARD (2026-09-25): the turn about +Y, degrees, that brings a body
   * authored off +Z to face +Z — measured ONCE by the load door on a new body,
   * replayed on every run before the bytes are parsed. Absent = as the file has it.
   */
  facing: z.object({ turnDeg: z.number().min(-180).max(180), reason: z.string().min(1) }).optional(),
  /** Empty array = no hand work declared. Required so "not declared" can never masquerade as "none". */
  hands: z.array(handSessionSchema),
  confirmations: z.array(confirmationSchema),
  /**
   * Tier-2 joint recentres (unbind-adjust-rebind: move the joint, keep the
   * mesh). OPTIONAL because every Document written before this field existed
   * is still a valid Document; absent and empty both mean "none declared".
   */
  recenters: z.array(jointRecenterSchema).optional(),
  /**
   * ADR-031 T2: tails grafted from tail zones (see tailDeclarationSchema).
   * OPTIONAL for the `recenters` reason; absent and empty both mean none.
   */
  tails: z.array(tailDeclarationSchema).optional(),
  /**
   * ADR-038 J2: soft-tissue leaves (see softTissueDeclarationSchema).
   * OPTIONAL for the `recenters` reason; absent and empty both mean none.
   */
  softTissue: z.array(softTissueDeclarationSchema).optional(),
  /**
   * ADR-039 T1: the confirmed hair (see hairDeclarationSchema). OPTIONAL for
   * the `recenters` reason; absent means nobody confirmed hair on this body.
   */
  hair: hairDeclarationSchema.optional(),
  /**
   * ADR-021 §7: the chest-integrity weight repair runs automatically at the
   * load door — a rig whose chest is SUPPOSED to ride the clavicles (a
   * costume, a mech) opts out HERE, with a reason a human wrote. Deliberate
   * and reasoned, never a silent skip; absent = the repair may run. OPTIONAL
   * so every Document written before this field existed stays valid.
   */
  chestRepair: z
    .object({
      optOut: z.literal(true),
      reason: z.string().min(1),
    })
    .optional(),
});
export type TargetSession = z.infer<typeof targetSessionSchema>;

/** An animation source: a specimen whose clips the work draws from. */
export const sourceSessionSchema = z.object({
  label: z.string().min(1),
  specimen: specimenRefSchema,
  selectSkeleton: z.string().min(1).optional(),
  declaredFps: z.number().positive().optional(),
});
export type SourceSession = z.infer<typeof sourceSessionSchema>;

/**
 * One clip applied to the target. The take is named, never indexed — a
 * Document naming a take that does not exist fails at load, loudly (§8);
 * clips[0] is how v2 auto-played Mixamo's vestigial empty take.
 */
export const clipSelectionSchema = z.object({
  /** Label of the source session the take comes from. */
  source: z.string().min(1),
  /** Take name within that source. BY NAME (§8). */
  take: z.string().min(1),
  /** Retarget coverage profile. Explicit, never defaulted silently. */
  profile: z.string().min(1),
});
export type ClipSelection = z.infer<typeof clipSelectionSchema>;

/**
 * A pose rotation must be a ROTATION: unit-length within float tolerance
 * (Codex P2, #52). A non-unit quaternion composes as a scaled/distorted
 * transform on the projected bone — a valid-looking Document visibly
 * corrupting a skinned mesh. Refused at the gate, never normalized silently
 * (§8: the value a run reproduces is the value the author wrote), and
 * refused HERE so both doors inherit it — a hand-authored file (parseDocument)
 * and the command log (pose.set re-runs this gate) alike. The tolerance
 * admits float32/JSON round-trip error (1e-3), not authoring mistakes.
 */
export const poseRotationSchema = quatDataSchema.superRefine((q, ctx) => {
  const magnitude = Math.hypot(q.x, q.y, q.z, q.w);
  if (Math.abs(magnitude - 1) > 1e-3) {
    ctx.addIssue({
      code: 'custom',
      message:
        `pose rotation (${q.x}, ${q.y}, ${q.z}, ${q.w}) is not a unit quaternion ` +
        `(|q| = ${magnitude.toFixed(6)}) — a non-unit value scales the bone instead of rotating it`,
    });
  }
});

/**
 * The Edit layer (§3.2) — the pose layer: per-bone local-rotation overrides
 * on the imported rest pose (ADR-007 §5.1 — not a bind edit, not a keyframe).
 * Written only through the command log's `pose.set`/`pose.clear`
 * (commands.ts); the frozen Document refuses every other writer.
 */
export const poseEditSchema = z.object({
  bone: z.string().min(1),
  rotation: poseRotationSchema,
});
export type PoseEdit = z.infer<typeof poseEditSchema>;

/**
 * Arm-space (ADR-017) — the tier-1 rotate-at-play character property: degrees
 * of constant abduction the display layer composes over the upper-arm clip
 * tracks. Band-gated HERE so both doors (a hand-authored file and the
 * `armSpace.set` command, which re-runs this gate) refuse a nonsense value
 * identically — same posture as the unit-quat gate above, and the same
 * canonical constant `armSpaceOffsets` refuses by (two doors, one value).
 * Absent = 0 = neutral. Never consulted by retarget or either export door.
 */
export const armSpaceDegreesSchema = z
  .number()
  .finite()
  .refine((d) => Math.abs(d) <= ARM_SPACE_MAX_DEGREES, {
    message: `arm-space is bounded to ±${ARM_SPACE_MAX_DEGREES}° — a value outside the band is refused, never clamped silently`,
  });

/**
 * Head-space — the Character panel's second tenant (head-space.ts header for
 * the seating story): degrees of constant cervical pitch (+ = erect). Since
 * ADR-030 a BIND property: the Document run reseats the Neck bone's bind by
 * it (its subtree following), so the exported character carries it and every
 * clip played on that character reads corrected. Band-gated HERE so both
 * doors (a hand-authored file and the `headSpace.set` command) refuse
 * identically — same two-doors posture as arm-space above. ABSENT is NOT
 * zero: absent means "the measured per-pair seating baseline"
 * (headSeatingDeltaDegrees), so heads come back to their authored reading
 * out of the box; a PRESENT value — zero included — is an explicit
 * declaration that replaces the baseline. The value is the character's
 * TOTAL seating from its authored rest (ADR-030 §14): a file that arrives
 * already seated declares what it wears in its marker, and a Document's
 * value is measured against the authored rest, not the loaded bind — so the
 * number a person sees survives export and reopen unchanged. Consulted by
 * the RUN (after retarget, so clips stay generic), never by retarget or the
 * animation-only export door.
 */
export const headSpaceDegreesSchema = z
  .number()
  .finite()
  .refine((d) => Math.abs(d) <= HEAD_SPACE_MAX_DEGREES, {
    message: `head-space is bounded to ±${HEAD_SPACE_MAX_DEGREES}° — a value outside the band is refused, never clamped silently`,
  });

/**
 * Material factors (ADR-020 T2, ratified §11: EXPORT-REAL) — per-material
 * glTF `metallicFactor`/`roughnessFactor` overrides. Unlike arm/head-space
 * these are the character's TRUTH, not a viewing preference: the display
 * applies them to the live materials and both export doors carry them into
 * the file. Factors MULTIPLY textures (glTF semantics) — zero texture work.
 *
 * Bands, gated here so both doors refuse identically (two doors, one value):
 * metallic stays in glTF's own [0,1]; roughness may reach 2 because the
 * wet-skin cure (Tripo bakes satin ~0.53 over skin) needs >1 — a >1 factor
 * is out of glTF range in the FILE, so the export derive step BAKES it into
 * the roughness channel and ships factor 1.0. A >1 roughness on a material
 * with no roughness texture to bake into refuses at export, loudly.
 *
 * `material` is the glTF material name — the file's own identity for it.
 * Whether the live character HAS that material is a surface question asked at
 * render/apply time (the armSpace posture); the kernel gate cannot know.
 */
export const METALLIC_FACTOR_MAX = 1;

/**
 * The absolute ceiling on a roughness factor. Raised from 2 to 32 on
 * 2026-08-26, because 2 was calibrated for one generation of source material
 * and a newer one cannot reach matte through it.
 *
 * MEASURED, on the metallicRoughness textures themselves (median of the
 * roughness channel):
 *
 *   Atlantian male 0.043 · Atlantian elder 0.051 · Atlantian female 0.149
 *   seaBee Trish   0.416 · seaBee Lizzy    0.498 · seaBee Jay        0.545
 *
 * A factor of 2 takes a seaBee's 0.5 to fully matte — which is why 2 was the
 * right number — and takes the Atlantian male's 0.043 to 0.086, still a
 * mirror. Reaching matte there needs about x23. Allen, field: "several of the
 * atlantians are super super shiny and i don't seem able to suppress all of
 * it." He was not missing a control; the control could not reach.
 *
 * The per-material ceiling is `roughnessCeilingFor` below — this constant only
 * says how far the schema will ever allow one to go. >1 stays spec-legal on
 * the way out because the derive step BAKES the factor into the texture, so
 * the shipped file keeps glTF's own [0,1] (ADR-020 T2).
 */
export const ROUGHNESS_FACTOR_MAX = 32;

/**
 * The reachable ceiling for ONE material: the factor that would take its
 * shipped roughness to fully matte, so the slider can always reach the answer
 * whatever generation the file came from.
 *
 * `shippedRoughness` is the median of the material's roughness TEXTURE, or
 * null when it has none — in which case the factor IS the roughness and glTF's
 * own [0,1] is the whole story (a >1 factor with no texture to bake into
 * refuses at export, so offering it would be offering a lie).
 *
 * A texture that is pure black in the roughness channel can never be
 * multiplied to matte at all; the ceiling opens to the maximum rather than
 * dividing by zero, and the shortfall is the user's to see.
 */
export function roughnessCeilingFor(shippedRoughness: number | null): number {
  if (shippedRoughness === null) return 1;
  if (!Number.isFinite(shippedRoughness) || shippedRoughness <= 0) return ROUGHNESS_FACTOR_MAX;
  // Never below 2: that was the previous ceiling and the satin class still
  // wants it, so no material loses reach by this becoming adaptive.
  return Math.min(ROUGHNESS_FACTOR_MAX, Math.max(2, Math.ceil(1 / shippedRoughness)));
}

export const metallicFactorSchema = z
  .number()
  .finite()
  .refine((v) => v >= 0 && v <= METALLIC_FACTOR_MAX, {
    message: `metallic factor is bounded to [0, ${METALLIC_FACTOR_MAX}] (glTF's own range) — refused, never clamped silently`,
  });

export const roughnessFactorSchema = z
  .number()
  .finite()
  .refine((v) => v >= 0 && v <= ROUGHNESS_FACTOR_MAX, {
    message: `roughness factor is bounded to [0, ${ROUGHNESS_FACTOR_MAX}] — refused, never clamped silently`,
  });

export const NORMAL_SCALE_MAX = 2;

/** glTF `normalTexture.scale` — 0 flattens the map, 2 doubles it. */
export const normalScaleSchema = z
  .number()
  .finite()
  .refine((v) => v >= 0 && v <= NORMAL_SCALE_MAX, {
    message: `normal scale is bounded to [0, ${NORMAL_SCALE_MAX}] — refused, never clamped silently`,
  });

/** glTF `occlusionTexture.strength` — the spec's own [0,1]. */
export const occlusionStrengthSchema = z
  .number()
  .finite()
  .refine((v) => v >= 0 && v <= 1, {
    message: `occlusion strength is bounded to [0, 1] (glTF's own range) — refused, never clamped silently`,
  });

const colorComponentSchema = z
  .number()
  .finite()
  .refine((v) => v >= 0 && v <= 1, {
    message: 'a color factor component is bounded to [0, 1] — refused, never clamped silently',
  });

/**
 * A LINEAR-space RGB factor triple, glTF's own shape and space
 * (`baseColorFactor` drops the alpha — alpha authoring is alphaMode
 * territory, deliberately not a factor edit). Surfaces converting from a
 * hex picker convert sRGB→linear BEFORE the Document; the Document speaks
 * the file's truth-space only.
 */
export const colorFactorSchema = z.tuple([colorComponentSchema, colorComponentSchema, colorComponentSchema]);
export type ColorFactor = z.infer<typeof colorFactorSchema>;

export const materialFactorEditSchema = z
  .object({
    /** The glTF material name — the file's identity for it. */
    material: z.string().min(1),
    metallicFactor: metallicFactorSchema.optional(),
    roughnessFactor: roughnessFactorSchema.optional(),
    normalScale: normalScaleSchema.optional(),
    occlusionStrength: occlusionStrengthSchema.optional(),
    baseColorFactor: colorFactorSchema.optional(),
    emissiveFactor: colorFactorSchema.optional(),
    /**
     * DETACH a channel from the metallicRoughness texture: false means "ignore
     * what the map says here and use the factor alone". Absent = attached, the
     * file's own behaviour.
     *
     * This exists because multiplying is the wrong instrument for a map that is
     * simply wrong. Tripo's newer class paints skin at roughness ~0.04 with
     * metallic ~0.5–0.96 — near-mirror metal. Raising the factor until that
     * reaches matte means multiplying by ~23, which amplifies every variation
     * in the map 23× and then clamps: a blotchy, unevenly matte surface. And
     * it saturates invisibly — three clamps effective roughness at 1.0, so past
     * the factor that first reaches matte the slider does nothing at all
     * (Allen, field: "the slider on rough doesn't seem to have any impact after
     * the initial 2").
     *
     * Detaching gives the clean answer the map cannot: one constant, evenly.
     * World's own material panel offers exactly this per channel, and it is
     * what cured these characters there — so the suite says the same thing in
     * both places.
     *
     * glTF ships ONE metallicRoughness texture with roughness in G and metallic
     * in B, so a detached channel is honoured on export by BAKING the constant
     * into that channel's plane, leaving the other channel's map intact.
     */
    metallicFromMap: z.boolean().optional(),
    roughnessFromMap: z.boolean().optional(),
  })
  .refine(
    (m) =>
      m.metallicFactor !== undefined ||
      m.roughnessFactor !== undefined ||
      m.normalScale !== undefined ||
      m.occlusionStrength !== undefined ||
      m.baseColorFactor !== undefined ||
      m.emissiveFactor !== undefined ||
      m.metallicFromMap !== undefined ||
      m.roughnessFromMap !== undefined,
    {
      message: 'a material edit carrying no factor edits nothing — refused rather than stored as dead data',
    },
  );
export type MaterialFactorEdit = z.infer<typeof materialFactorEditSchema>;

export const editsSchema = z.object({
  poses: z.array(poseEditSchema),
  armSpace: armSpaceDegreesSchema.optional(),
  headSpace: headSpaceDegreesSchema.optional(),
  /**
   * ADR-038 R3 (ruled 2026-09-24): the tail spring's settings — on, stiffness,
   * drag, gravity, muscle, mood — written by `tailSpring.set` (undoable, saved,
   * and carried into the export's spring declaration). ABSENT = the tail's
   * source decides: a grown tail wears the prior's defaults, a file's spring
   * wears what the file declares (the arm-space-file posture). A per-body
   * factor: a new target resets it with the other edits.
   */
  tailSpring: tailSpringSettingsSchema.optional(),
  /** Absent = no factor edits: every material wears what the file shipped. */
  materials: z
    .array(materialFactorEditSchema)
    .superRefine((materials, ctx) => {
      // A material name is an identity (the clip-name law, worn here): two
      // entries for one material would make "the" factor ambiguous.
      const seen = new Set<string>();
      for (const m of materials) {
        if (seen.has(m.material)) {
          ctx.addIssue({ code: 'custom', message: `material "${m.material}" appears twice — one material holds one edit` });
        }
        seen.add(m.material);
      }
    })
    .optional(),
});
export type Edits = z.infer<typeof editsSchema>;

/**
 * A keyframe id: `k1, k2, …` — minted by the track's stored counter (ADR-007
 * §12.2, DECIDED): deterministic from Document state, so replay mints
 * identically. Never content-derived — content ids collide on identical keys
 * and re-derive when a key's value is edited, both identity-breaking.
 */
export const keyIdSchema = z.string().regex(/^k[1-9]\d*$/);

/**
 * One identified keyframe (ADR-006 §13.4: "each key an addressable identity,
 * not a coincidence of index across two parallel arrays"). Time is SECONDS
 * from clip start — fps is the DISPLAY declaration, never the storage unit,
 * so imported clips can materialize into this schema without quantizing.
 */
export const keyframeSchema = z.object({
  id: keyIdSchema,
  time: z.number().finite().nonnegative(),
  rotation: poseRotationSchema,
});
export type Keyframe = z.infer<typeof keyframeSchema>;

/**
 * A bone's key track. `nextKey` is the §12.2 stored counter: never
 * decremented, so a deleted id is never re-minted within a lineage — a live
 * reference (a selection, a log entry) can never silently point at a
 * different key wearing a dead key's name. Tracks persist once created (an
 * empty keys array keeps the counter); rotation-only in v1, the pose
 * vocabulary keyed over time.
 */
export const keyTrackSchema = z.object({
  bone: z.string().min(1),
  nextKey: z.number().int().min(1),
  keys: z.array(keyframeSchema),
});
export type KeyTrack = z.infer<typeof keyTrackSchema>;

/** An authored clip (§13.4). Its name is its identity — unique, never indexed. */
export const authoredClipSchema = z.object({
  name: z.string().min(1),
  tracks: z.array(keyTrackSchema),
});
export type AuthoredClip = z.infer<typeof authoredClipSchema>;

/**
 * A ZONE (ADR-031 §3.1): a named face set on the target mesh, carrying up to
 * three declarations (look, physics class, bones — later tiers) and, always,
 * its provenance. Faces are indices into the target's primitive, valid only
 * against the mesh they were cut on — `meshFingerprint` is what lets the door
 * that applies a zone REFUSE a regenerated character by name instead of
 * lighting the wrong triangles (D2).
 *
 * `sources` is the history of how the face set came to be: a measured
 * proposal (the unarmatured-flesh detector at some threshold), a human edit
 * (the flyout's grow/shrink, the wand), an imported declaration (a file that
 * carried zones). Never empty: a zone nobody can account for is not a zone.
 *
 * `intent` is what a PERSON said this flesh is — the flyout's "this is a
 * tail" / "this is cloth" (D8). It is a human act, so it rides with
 * `confirmedBy` (the `human:` namespace, the same trust boundary the digit
 * confirmations wear); the detector's own reading lives in `shape` and never
 * masquerades as an answer.
 */
export const zoneIdSchema = z.string().regex(/^z[1-9]\d*$/);
export const zoneSourceSchema = z.object({
  kind: z.enum(['measured-proposal', 'human-edit', 'imported-declaration']),
  /** What measured it or who edited it — e.g. `unarmatured@2×`, `flyout:grow`. */
  detail: z.string().min(1),
});
export const meshFingerprintSchema = z.object({
  vertexCount: z.number().int().positive(),
  /** Triangles, because face indices address the INDEX buffer — a re-indexed mesh must refuse (Codex P1, #136). */
  faceCount: z.number().int().positive(),
  /** Over positions then indices. */
  positionsHash: z.string().regex(/^[0-9a-f]{16}$/),
});
/** `stub`: a possible stub tail a person pinned (2026-09-24) — one or two bones, a springy single leaf. */
export const zoneShapeSchema = z.enum(['tube', 'sheet', 'blob', 'stub']);
/**
 * `softTissue` (ADR-038 J2): the flesh a breast / belly / glute leaf owns — a declared zone overrides the detector.
 * `hair` (ADR-039): the region `target.hair` names — written only by `hair.confirm`, never by `zone.confirm`.
 */
export const zoneIntentSchema = z.enum(['tail', 'cloth', 'softTissue', 'hair']);
export const zoneSchema = z
  .object({
    id: zoneIdSchema,
    name: z.string().min(1),
    faces: z.array(z.number().int().nonnegative()).min(1),
    meshFingerprint: meshFingerprintSchema,
    sources: z.array(zoneSourceSchema).min(1),
    /** The detector's reading of the cluster's shape — evidence, not a declaration. */
    shape: zoneShapeSchema.optional(),
    /** What a person said it is. Absent = not yet answered. */
    intent: zoneIntentSchema.optional(),
    confirmedBy: z.string().regex(/^human:\S+$/).optional(),
  })
  .superRefine((zone, ctx) => {
    if (new Set(zone.faces).size !== zone.faces.length) {
      ctx.addIssue({ code: 'custom', message: `zone "${zone.name}" lists a face twice — a face set is a set` });
    }
    if (zone.intent !== undefined && zone.confirmedBy === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: `zone "${zone.name}" declares intent "${zone.intent}" with nobody confirming it — intent is a human act`,
      });
    }
  });
export type Zone = z.infer<typeof zoneSchema>;
export type ZoneSource = z.infer<typeof zoneSourceSchema>;
export type ZoneIntent = z.infer<typeof zoneIntentSchema>;

/**
 * ADR-033 — the World package. The vocabulary below is World's ADR-189 sets
 * schema, reused verbatim (its own rule: "reuse tokens, never synonymize"),
 * so a binding written here is ingested by their resolver unchanged.
 */
export const slugSchema = z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/);
export const clipIdSchema = slugSchema;
export const selectorPathSchema = z.string().regex(/^(\*|[a-z0-9]+(-[a-z0-9]+)*(\/[a-z0-9]+(-[a-z0-9]+)*)*)$/);
export const contractKeySchema = z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/);
export const setStateSchema = z.string().regex(/^[a-z][a-z0-9-]*$/);
export const facetBodySchema = z.enum(['human', 'monster', 'quadruped']);
export const facetPresentationSchema = z.enum(['feminine', 'masculine', 'neutral']);
export const packageRoleSchema = z.enum(['avatar', 'npc']);
export type PackageRole = z.infer<typeof packageRoleSchema>;
export const packageFacetsSchema = z.strictObject({ body: facetBodySchema, presentation: facetPresentationSchema });
export type PackageFacets = z.infer<typeof packageFacetsSchema>;

/**
 * World's first-login avatar picker filters (World #2125 part C) — NOT the
 * ADR-189 `facets` above, which feed the animation-set path. World stores
 * these as PREFIXED roster tags (`presentation:feminine`, `family:pirates`);
 * the author sets them here and the composer expands them (picker-facets.ts).
 * Lenient on purpose: the values only need to be tag-shaped slugs here, and
 * an unknown presentation/kind/style is a REFUSAL in the composer's list
 * (where it reaches the eye), never a Document that will not open.
 */
export const pickerFacetsSchema = z.strictObject({
  /** Several allowed (Lou is feminine AND androgynous). */
  presentation: z.array(slugSchema).min(1).optional(),
  kind: slugSchema.optional(),
  style: slugSchema.optional(),
  /** Several allowed; data-driven chips in World, so an unknown one is a warning, not a refusal. */
  family: z.array(slugSchema).min(1).optional(),
});
export type PickerFacets = z.infer<typeof pickerFacetsSchema>;

/** ADR-189 `DrawPolicy`, verbatim. */
export const drawPolicySchema = z.strictObject({
  mode: z.enum(['random', 'sequential', 'single']).optional(),
  weights: z.array(z.number().positive()).optional(),
  minHoldS: z.number().nonnegative().optional(),
});

/** ADR-189 `SetBinding`, verbatim: weights, when present, parallel the pool. */
export const setBindingSchema = z
  .strictObject({
    key: contractKeySchema,
    selector: selectorPathSchema,
    state: setStateSchema.optional(),
    clips: z.array(clipIdSchema).min(1),
    draw: drawPolicySchema.optional(),
    notes: z.string().optional(),
  })
  .superRefine((b, ctx) => {
    if (b.draw?.weights !== undefined && b.draw.weights.length !== b.clips.length) {
      ctx.addIssue({ code: 'custom', path: ['draw', 'weights'], message: `${b.draw.weights.length} weights for ${b.clips.length} clips` });
    }
  });
export type SetBinding = z.infer<typeof setBindingSchema>;

/** A pin: a binding without its selector — the selector is THIS avatar's entity path, derived at compose. */
export const packagePinSchema = z
  .strictObject({
    key: contractKeySchema,
    state: setStateSchema.optional(),
    clips: z.array(clipIdSchema).min(1),
    draw: drawPolicySchema.optional(),
    notes: z.string().optional(),
  })
  .superRefine((b, ctx) => {
    if (b.draw?.weights !== undefined && b.draw.weights.length !== b.clips.length) {
      ctx.addIssue({ code: 'custom', path: ['draw', 'weights'], message: `${b.draw.weights.length} weights for ${b.clips.length} clips` });
    }
  });
export type PackagePin = z.infer<typeof packagePinSchema>;

/**
 * What the author decided about the package (ADR-033, F6): LENIENT while in
 * progress — an npc role may wait for its archetype, a take may not be
 * starred yet. The strict shape is the record (`package.ts`), composed from
 * these choices plus measurements; the readiness gate lives there. What
 * refuses HERE is only what could never be true: a starred take that is not
 * a take, a role twice, two pins for one (key, state), an npc block without
 * the npc role.
 */
export const packageChoicesSchema = z
  .strictObject({
    /** Absent = derived from the name at package time, then written back so it stays stable. */
    id: slugSchema.optional(),
    /** Absent = the Document's name. */
    name: z.string().min(1).optional(),
    artist: z.string().min(1).optional(),
    tags: z.array(z.string().min(1)).optional(),
    /**
     * The avatar picker's filters, author-set. OPTIONAL (the `zones`
     * precedent — every earlier Document stays valid); absent per facet = the
     * replaced roster row's value carries over at compose.
     */
    pickerFacets: pickerFacetsSchema.optional(),
    roles: z.array(packageRoleSchema).min(1),
    facets: packageFacetsSchema,
    /** Declared: World scales the body to it (delivery §2a). A default is a declaration, not an absence. */
    heightMeters: declaredNumberSchema,
    selectionClip: clipIdSchema.optional(),
    pins: z.array(packagePinSchema),
    npc: z.strictObject({ archetype: selectorPathSchema, note: z.string().optional() }).optional(),
    /** Package-relative filenames; the PNGs live beside the record, never in it. */
    heroTakes: z.array(z.string().min(1)),
    heroFavourite: z.string().min(1).optional(),
  })
  .superRefine((c, ctx) => {
    if (new Set(c.roles).size !== c.roles.length) {
      ctx.addIssue({ code: 'custom', path: ['roles'], message: 'a role appears twice' });
    }
    if (c.heroFavourite !== undefined && !c.heroTakes.includes(c.heroFavourite)) {
      ctx.addIssue({ code: 'custom', path: ['heroFavourite'], message: `"${c.heroFavourite}" is not one of the takes` });
    }
    if (c.npc !== undefined && !c.roles.includes('npc')) {
      ctx.addIssue({ code: 'custom', path: ['npc'], message: 'an npc block without the npc role is a contradiction' });
    }
    const seen = new Set<string>();
    c.pins.forEach((p, i) => {
      const tuple = `${p.key}@${p.state ?? 'neutral'}`;
      if (seen.has(tuple)) {
        ctx.addIssue({ code: 'custom', path: ['pins', i], message: `two pins for ${tuple} — one key, one pool per state` });
      }
      seen.add(tuple);
    });
  });
export type PackageChoices = z.infer<typeof packageChoicesSchema>;

export const documentSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  /** Human-readable work title. */
  name: z.string().min(1),
  /**
   * Timeline display rate (§13.4) — declared with provenance, because it does
   * not exist in v2 at all; frame math there is a hardcoded `* 30`. A default
   * is a declaration, not an absence (§8).
   */
  fps: declaredNumberSchema,
  target: targetSessionSchema,
  sources: z.array(sourceSessionSchema),
  clipSelections: z.array(clipSelectionSchema),
  /** The timeline slice (§13.4): authored clips whose keys carry identities. */
  clips: z.array(authoredClipSchema),
  edits: editsSchema,
  /**
   * ADR-031 zones on the target mesh. OPTIONAL because every Document written
   * before this field existed is still a valid Document (the `recenters`
   * precedent); absent and empty both mean "none declared".
   */
  zones: z.array(zoneSchema).optional(),
  /**
   * ADR-033: what the author decided about the World package. OPTIONAL for
   * the same reason as `zones`; absent = nothing decided, which the package
   * door refuses by name rather than guessing.
   */
  package: packageChoicesSchema.optional(),
});
export type AnimatorDocument = z.infer<typeof documentSchema>;

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === 'object') {
    for (const key of Object.getOwnPropertyNames(value)) {
      deepFreeze((value as Record<string, unknown>)[key]);
    }
    Object.freeze(value);
  }
  return value;
}

/**
 * Validate raw data into a Document, loudly — the machine-check that keeps
 * app, agent, and corpus honest about one shape (§4). Structural rules beyond
 * the schema: labels are identities and must be unique; every clip selection
 * names a source that exists; every confirmation names a declared hand.
 * The returned Document is DEEP-FROZEN: an edit applied outside the command
 * log is a refused write, not a discouraged one (§10.5).
 */
export function parseDocument(raw: unknown): AnimatorDocument {
  const parsed = documentSchema.safeParse(raw);
  if (!parsed.success) {
    throw new SchemaValidationError(
      'Document',
      parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`),
    );
  }
  const doc = parsed.data;
  const structural: string[] = [];

  const labels = new Set<string>([doc.target.label]);
  for (const s of doc.sources) {
    if (labels.has(s.label)) structural.push(`duplicate session label "${s.label}"`);
    labels.add(s.label);
  }

  const sourceLabels = new Set(doc.sources.map((s) => s.label));
  for (const c of doc.clipSelections) {
    if (!sourceLabels.has(c.source)) {
      structural.push(`clip selection references unknown source "${c.source}"`);
    }
  }

  // A zone's id and name are both identities (ADR-031): two zones wearing one
  // name would make "the tail" ambiguous on the flyout and in the file.
  const zoneIds = new Set<string>();
  const zoneNames = new Set<string>();
  for (const zone of doc.zones ?? []) {
    if (zoneIds.has(zone.id)) structural.push(`duplicate zone id "${zone.id}"`);
    if (zoneNames.has(zone.name)) structural.push(`duplicate zone name "${zone.name}"`);
    zoneIds.add(zone.id);
    zoneNames.add(zone.name);
  }

  // A TAIL (ADR-031 §3.3a) names a zone a PERSON called a tail, and there is
  // one chain: the sixteen BBSA slots are one tail's (T2). A zone carrying a
  // tail therefore cannot be deleted or re-declared cloth underneath it —
  // those refuse here, naming the tail, so the zone verbs need no special case.
  const tails = doc.target.tails ?? [];
  if (tails.length > 1) {
    structural.push(`${tails.length} tails declared — T2 grafts one chain (the sixteen Tail slots); withdraw all but one`);
  }
  for (const t of tails) {
    const zone = (doc.zones ?? []).find((z) => z.id === t.zoneId);
    if (zone === undefined) {
      structural.push(`tail declares zone "${t.zoneId}", which is not a zone — withdraw the tail before removing its zone`);
    } else if (zone.intent !== 'tail') {
      structural.push(
        `zone "${zone.name}" carries a tail but is ${zone.intent === undefined ? 'unconfirmed' : `declared ${zone.intent}`} — ` +
          `a chain grows only in flesh a person called a tail; withdraw the tail first`,
      );
    }
    // A STUB's band is one or two bones; every other tail's is the chain's.
    const [lo, hi] = zone?.shape === 'stub' ? [STUB_COUNT_MIN, STUB_COUNT_MAX] : [TAIL_COUNT_MIN, TAIL_COUNT_MAX];
    if (!Number.isInteger(t.count.value) || t.count.value < lo || t.count.value > hi) {
      structural.push(
        `tail on zone "${zone?.name ?? t.zoneId}" (${zone?.shape ?? 'unmeasured'}, called a tail): ${t.count.value} bones is outside ` +
          `the ${zone?.shape === 'stub' ? 'stub' : 'tail'} band [${lo}, ${hi}]`,
      );
    }
    if (t.resolution !== undefined && (!Number.isInteger(t.resolution.value) || t.resolution.value < 8 || t.resolution.value > 128)) {
      structural.push(`tail on zone "${zone?.name ?? t.zoneId}": resolution ${t.resolution.value} outside integer band 8–128`);
    }
  }

  // A SOFT-TISSUE LEAF (ADR-038 J2): one per region (each region is one BBSA
  // slot), and a declared zone must be one a PERSON called soft tissue — a
  // zone carrying a leaf cannot be deleted or re-declared underneath it.
  const softRegions = new Set<string>();
  for (const s of doc.target.softTissue ?? []) {
    if (softRegions.has(s.region)) structural.push(`soft-tissue region ${s.region} declared twice — one leaf per region`);
    softRegions.add(s.region);
    if (s.zoneId === undefined) continue;
    const zone = (doc.zones ?? []).find((z) => z.id === s.zoneId);
    if (zone === undefined) {
      structural.push(`${s.region} leaf declares zone "${s.zoneId}", which is not a zone — withdraw the leaf before removing its zone`);
    } else if (zone.intent !== 'softTissue') {
      structural.push(
        `zone "${zone.name}" carries the ${s.region} leaf but is ${zone.intent === undefined ? 'unconfirmed' : `declared ${zone.intent}`} — ` +
          `a leaf grows only over flesh a person called soft tissue; withdraw the leaf first`,
      );
    }
  }

  // HAIR (ADR-039): the declaration and its zone come and go together. A hair
  // zone nobody's hair names is an orphan (and `zone.confirm` cannot mint one —
  // this refuses it); a declaration naming a zone that is gone or not hair
  // refuses, so the zone verbs need no special case.
  const hair = doc.target.hair;
  const hairZones = (doc.zones ?? []).filter((z) => z.intent === 'hair');
  if (hair !== undefined) {
    const zone = (doc.zones ?? []).find((z) => z.id === hair.zoneId);
    if (zone === undefined) {
      structural.push(`hair declares zone "${hair.zoneId}", which is not a zone — withdraw the hair before removing its zone`);
    } else if (zone.intent !== 'hair') {
      structural.push(
        `zone "${zone.name}" carries the hair but is ${zone.intent === undefined ? 'unconfirmed' : `declared ${zone.intent}`} — withdraw the hair first`,
      );
    }
  }
  for (const z of hairZones) {
    if (hair === undefined || hair.zoneId !== z.id) {
      structural.push(`zone "${z.name}" is marked hair but no hair declaration names it — hair is confirmed with its skull (hair.confirm)`);
    }
  }

  // One bone, one pose value — the upsert semantic applyCommand already
  // enforces on the edit path, enforced here on the load path too. A duplicate
  // is ambiguous truth: which rotation IS the bone's pose, and which snapshot
  // is its rest? (Codex P2, #54 round 3 — a dup made the driver record the
  // first pose as the second's "rest".)
  const posedBones = new Set<string>();
  for (const p of doc.edits.poses) {
    if (posedBones.has(p.bone)) structural.push(`duplicate pose edit for bone "${p.bone}" — one bone, one pose value`);
    posedBones.add(p.bone);
  }

  // The timeline's declared rate: a display fact with a sane band. 240 admits
  // every capture rate in the corpus; 0/negative/absurd values refuse naming
  // the band rather than rendering a timeline that lies about time.
  if (!Number.isFinite(doc.fps.value) || doc.fps.value <= 0 || doc.fps.value > 240) {
    structural.push(`fps ${doc.fps.value} outside the declared band (0, 240]`);
  }

  const clipNames = new Set<string>();
  for (const clip of doc.clips) {
    if (clipNames.has(clip.name)) structural.push(`duplicate clip "${clip.name}" — clip names are identities`);
    clipNames.add(clip.name);
    const trackBones = new Set<string>();
    for (const track of clip.tracks) {
      const where = `clip "${clip.name}" track "${track.bone}"`;
      if (trackBones.has(track.bone)) {
        structural.push(`duplicate track for bone "${track.bone}" in clip "${clip.name}" — one bone, one track`);
      }
      trackBones.add(track.bone);
      const ids = new Set<string>();
      let prevTime = -Infinity;
      for (const key of track.keys) {
        if (ids.has(key.id)) structural.push(`${where}: duplicate key id "${key.id}"`);
        ids.add(key.id);
        // An id at or above the counter would be re-minted by a future key.set
        // — two keys wearing one name, identity broken (§12.2).
        if (Number(key.id.slice(1)) >= track.nextKey) {
          structural.push(
            `${where}: key id "${key.id}" is not below the stored counter (nextKey ${track.nextKey}) — ` +
              `the counter would mint it again`,
          );
        }
        // Times are judged at PLAYBACK precision (Float32 — what the mixer's
        // typed arrays hold), while the STORED value stays the author's (§8:
        // never normalized silently). Two float64 times the player cannot
        // tell apart are the same instant — accepting both would commit an
        // edit the projection then refuses as non-ascending (Codex P2, #61
        // round 2). Float32-strict-ascending implies float64 ascending, so
        // one check covers both. A time outside Float32's finite range is
        // unplayable outright.
        if (!Number.isFinite(Math.fround(key.time))) {
          structural.push(
            `${where}: key time ${key.time} is not finite at playback precision (Float32) — unplayable`,
          );
        }
        if (Math.fround(key.time) <= Math.fround(prevTime)) {
          structural.push(
            `${where}: keys must ascend strictly at playback precision (Float32) — ` +
              `${key.time} follows ${prevTime} and the player cannot tell them apart`,
          );
        }
        prevTime = key.time;
      }
    }
  }

  const handBones = new Set<string>();
  for (const h of doc.target.hands) {
    if (handBones.has(h.handBone)) structural.push(`duplicate hand session for bone "${h.handBone}"`);
    handBones.add(h.handBone);
    if (h.forearmBone === h.handBone) {
      structural.push(`hand "${h.handBone}": forearmBone must differ from handBone`);
    }
    if (!Number.isInteger(h.resolution.value) || h.resolution.value < 8 || h.resolution.value > 128) {
      structural.push(`hand "${h.handBone}": resolution ${h.resolution.value} outside integer band 8–128 (ADR-010 §11)`);
    }
  }

  // One pivot, one declaration. Two recentres on the same bone would apply in
  // array order and the loser would be invisible — the same silent-resolution
  // hazard the confirmation keys below refuse (Codex P2, #50).
  const recenterBones = new Set<string>();
  for (const r of doc.target.recenters ?? []) {
    if (recenterBones.has(r.bone)) {
      structural.push(`duplicate recenter for bone "${r.bone}" — one pivot, one declaration`);
    }
    recenterBones.add(r.bone);
  }

  const confirmationKeys = new Set<string>();
  const pinnedKnuckles = new Set<string>();
  const pinnedTips = new Set<string>();
  for (const c of doc.target.confirmations) {
    // One value per affordance per hand: contradictory duplicates must refuse
    // here, never resolve by first-match order (Codex P2, #50). `digitAssignment`
    // and `digitKnuckle` are the targets that legitimately repeat per hand —
    // once per digit — so they are keyed by DIGIT, which is what may not repeat.
    const pin = c.target === 'digitAssignment' || c.target === 'digitKnuckle' ? parseDigitPin(c.value) : null;
    const key = pin === null ? `${c.target}@${c.scope}` : `${c.target}@${c.scope}@${pin.digit}`;
    if (confirmationKeys.has(key)) {
      structural.push(
        pin === null
          ? `duplicate ${c.target} confirmation for hand "${c.scope}" — one value per affordance`
          : c.target === 'digitKnuckle'
            ? `digit "${pin.digit}" has two knuckle pins on hand "${c.scope}" — one digit, one knuckle`
            : `digit "${pin.digit}" is pinned twice on hand "${c.scope}" — one digit, one pin`,
      );
    }
    confirmationKeys.add(key);
    if (!handBones.has(c.scope)) {
      structural.push(`confirmation (${c.target}) names undeclared hand "${c.scope}"`);
    }
    if (c.target === 'thumbBranch' && !/^branch-\d+$/.test(c.value)) {
      structural.push(`thumbBranch confirmation value "${c.value}" is not a branch id`);
    }
    if (c.target === 'digitAssignment') {
      if (isLegacyDigitPin(c.value)) {
        // ADR-022 D3, ruled by Allen 2026-08-11: REFUSE, naming the migration.
        // The alternative — converting at the stored resolution — would re-aim a
        // person's answer at whatever geometry that branch id labels today, and
        // nobody would re-examine it. A graft is one-shot, so the only Documents
        // this can touch are un-grafted ones mid-flight.
        structural.push(
          `digitAssignment confirmation "${c.value}" on hand "${c.scope}" uses the retired branch-keyed ` +
            `form — digit targets are now stored as BIND-WORLD tips ("<Digit>@<x>,<y>,<z>", ADR-022 §3.2). ` +
            `Re-pin this hand: branch ids renumber per analysis, so the old value cannot be converted ` +
            `without silently re-aiming it.`,
        );
      } else if (pin === null || !(DIGIT_NAMES as readonly string[]).includes(pin.digit)) {
        structural.push(
          `digitAssignment confirmation value "${c.value}" is not "<digit>@<x>,<y>,<z>" ` +
            `(digits: ${DIGIT_NAMES.join(', ')})`,
        );
      } else {
        // Two digits on ONE place is the tip-space twin of the old two-digits-one-branch
        // collision: a person cannot have meant both, and first-match order must not decide.
        const tipKey = `${c.scope}@${formatDigitPin('', pin.tip)}`;
        if (pinnedTips.has(tipKey)) {
          structural.push(`two digits are pinned to the same tip on hand "${c.scope}" — one place, one digit`);
        }
        pinnedTips.add(tipKey);
      }
    }
    if (c.target === 'digitKnuckle') {
      if (pin === null || !(DIGIT_NAMES as readonly string[]).includes(pin.digit)) {
        structural.push(
          `digitKnuckle confirmation value "${c.value}" is not "<digit>@<x>,<y>,<z>" ` +
            `(digits: ${DIGIT_NAMES.join(', ')})`,
        );
      } else {
        // Two digits on ONE knuckle (Codex P2, #97): the knuckle twin of the
        // two-digits-one-tip collision — placement would seat both MCPs on the
        // same point, collapsing the row.
        const knuckleKey = `${c.scope}@${formatDigitPin('', pin.tip)}`;
        if (pinnedKnuckles.has(knuckleKey)) {
          structural.push(`two digits are pinned to the same knuckle on hand "${c.scope}" — one place, one digit`);
        }
        pinnedKnuckles.add(knuckleKey);
      }
    }
    if (c.target === 'handProfile' && !(HAND_PROFILE_VALUES as readonly string[]).includes(c.value)) {
      structural.push(
        `handProfile confirmation value "${c.value}" is not a hand profile (one of: ${HAND_PROFILE_VALUES.join(', ')})`,
      );
    }
    if (c.target === 'thumbOpposition') {
      const deg = Number(c.value);
      if (!/^-?\d+$/.test(c.value) || !Number.isFinite(deg) || deg < -180 || deg > 180) {
        structural.push(`thumbOpposition confirmation value "${c.value}" is not an integer number of degrees in −180…180`);
      }
    }
    if (c.target === 'wristStation') {
      const st = parseDigitPin(c.value);
      if (st === null || st.digit !== WRIST_STATION_NAME) {
        structural.push(`wristStation confirmation value "${c.value}" is not "${WRIST_STATION_NAME}@<x>,<y>,<z>" (a bind-world point)`);
      }
    }
  }

  // A KNUCKLE NAMES A ROW POSITION ON A DIGIT SOMEBODY PINNED (ADR-024 §2.2).
  // One without a matching tip assignment is an orphan — placement would have
  // a base and no digit to hang it on — and first-match tolerance would let a
  // renamed or removed tip strand its knuckle silently. Cross-checked here so
  // the refusal names the digit.
  const assignedByScope = new Map<string, Set<string>>();
  for (const c of doc.target.confirmations) {
    if (c.target !== 'digitAssignment') continue;
    const pin = parseDigitPin(c.value);
    if (pin === null) continue;
    const set = assignedByScope.get(c.scope) ?? new Set<string>();
    set.add(pin.digit);
    assignedByScope.set(c.scope, set);
  }
  for (const c of doc.target.confirmations) {
    if (c.target !== 'digitKnuckle') continue;
    const pin = parseDigitPin(c.value);
    if (pin === null) continue;
    if (!(assignedByScope.get(c.scope)?.has(pin.digit) ?? false)) {
      structural.push(
        `digitKnuckle for "${pin.digit}" on hand "${c.scope}" has no matching digitAssignment — ` +
          `a knuckle belongs to a pinned digit; pin the tip, or remove the knuckle`,
      );
    }
  }

  if (structural.length > 0) throw new SchemaValidationError('Document', structural);
  return deepFreeze(doc);
}

